import React, { useState, useEffect, useRef } from 'react';
import i18nIsoCountries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { useDispatch, useSelector } from 'react-redux';
import { notification, Table, Input, Button, Modal, theme } from 'antd';
import {
  PlusOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { contractual_informationOp } from 'store/ducks/DataEntry/ContractualInformation';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import {
  IContractulInformation,
  ICosts,
  EditedData,
} from 'store/ducks/DataEntry/ContractualInformation/types';
import useCountries from 'utils/hooks/fetchHooks/useCountries';
import useFilters from 'utils/hooks/useFilters';
import DynamicField from 'components/DynamicCalendarField/DynamicField';
import CustomSelect from 'components/CustomSelect';
import AdditionalInformation from 'components/AdditionalInformation/AdditionalInformation';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { setSelecedEntity_Contractual_Information } from 'store/ducks/DataEntry/ContractualInformation/contractualInformationSlice';
import { entityOp } from 'store/ducks/DataEntry/Entities';
import { requiredFieldsForContractualArrangement } from 'constants/requiredFields';
import SaveButton from 'components/SaveButton/SaveButton';
import useTooltipClickOutside from 'utils/hooks/useTooltipClickOutside';
import * as Styled from '../../DataEntry.styled';
import { useTranslation } from 'react-i18next';
import { deleteRecord } from 'utils/hooks/deleteRecord';

const ContractualInformation: React.FC = () => {
  const [data, setData] = useState<IContractulInformation[]>([]);
  const [editedData, setEditedData] = useState<EditedData>({});
  const [changedData, setChangedData] = useState<Record<string, any>[]>([]);
  const [terminatReasons, setTerminatReasons] = useState([]);
  const [contractsData, setContractsData] = useState<any[]>([]);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const topScrollInnerRef = useRef<HTMLDivElement>(null);
  const tableContentRef = useRef<HTMLDivElement | null>(null);
  const [contractualArrangementTypes, setContractualArrangementTypes] =
    useState([]);
  const { countries } = useCountries();
  const { confirm } = Modal;
  const [isTooltipVisible, setIsTooltipVisible] = useState<{
    [key: string]: boolean;
  }>({});
  const { query } = useFilters();
  const { t } = useTranslation();
  const [currencies, setCurrencies] = useState<
    { label: string; value: string }[]
  >([]);
  const { useToken } = theme;
  const { token } = useToken();

  const intraGroup = [
    { label: 'yes', value: true },
    { label: 'no', value: false },
  ];

  const {
    selectedBranchForContractualInformation,
    selectedICTForContractualInformation,
    selectedTPPForContractualInformation,
    selectedEntityForContractualInformation,
  } = useSelector((state: any) => state.contractualInformation);

  const isLoggedIn = localStorage.getItem('authToken');
  i18nIsoCountries.registerLocale(enLocale);
  const dispatch = useDispatch();

  const fetchTerminatReasons = async () => {
    try {
      const response = await contractual_informationOp.fetchTerminatReasons();
      if (response?.data.terminat_reasons) {
        const formattedOptions = response.data.terminat_reasons.map(
          (reasons: { name: any; id: any }) => ({
            label: reasons.name,
            value: reasons.id,
          }),
        );
        setTerminatReasons(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchContractualArrangementTypes = async () => {
    try {
      const response =
        await contractual_informationOp.fetchContractualArrangementTypes(
          selectedEntityForContractualInformation.entityId,
        );
      if (response?.data.contractual_arrangement_types) {
        const formattedOptions =
          response.data.contractual_arrangement_types.map(
            (reasons: { name: any; id: any }) => ({
              label: reasons.name,
              value: reasons.id,
            }),
          );
        setContractualArrangementTypes(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchContractualInformation =
    contractual_informationOp.useFetchContractualInformation();

  useEffect(() => {
    if (!selectedEntityForContractualInformation) {
      const selectedEntity = {
        entityId: query.entityId ? Number(query.entityId) : 0,
        entity: query.entity ? String(query.entity) : '',
      };
      dispatch(setSelecedEntity_Contractual_Information(selectedEntity));
    }
  }, [query]);

  const fetchContracts = async () => {
    try {
      const response = await fetchContractualInformation();
      const data = response?.data.arrangements;
      const mappedData = data.map((item: any, index: number) => ({
        key: index,
        contract_name: item.contract_name,
        contract_description: item.contract_description,
        terminatReason_id: item.terminat_reason.id,
        terminatReason_name: item.terminat_reason.name,
        terminatReason_description: item.terminat_reason.description,
        typeofContractualArrangement_id: item.contract_type_arrangement.id,
        typeofContractualArrangement_name: item.contract_type_arrangement.name,
        typeofContractualArrangement_description:
          item.contract_type_arrangement.description,
        costs: item.costs
          ? item.costs.map((cost: any) => ({
              costs_id: cost.id,
              costs_currency_amount_reported: cost.currency_amount_reported,
              costs_annual_expense: cost.annual_expense,
              costs_total_annual_expense: cost.total_annual_expense,
              costs_description: cost.description,
              costs_currency_id: cost?.currency?.id,
              costs_contractual_arrangement_id: cost.contractual_arrangement_id,
              costs_start_date: cost.start_date,
              costs_end_date: cost.end_date,
              currency_name: cost?.currency?.currency_name,
              currency_description: cost?.currency?.description,
              currency_id: cost?.currency?.id,
            }))
          : [],
        country_id: item.governing_law_country.id,
        country_country_name: item.governing_law_country.country_name,
        country_iso_code: item.governing_law_country.iso_code,
        country_description: item.governing_law_country.description,
        contractualArrangement_id: item.id,
        contractualArrangement_arrangement_reference_number:
          item.arrangement_reference_number,
        contractualArrangement_start_date: item.start_date,
        contractualArrangement_end_date: item.end_date,
        contractualArrangement_terminat_reason_id: item.terminat_reason.id,
        contractualArrangement_contract_type_id: item.contract_type?.id,
        contractualArrangement_intra_group: item.intra_group,
        contractualArrangement_contract_type_arrangement_id:
          item.contract_type_arrangement.id,
        contractualArrangement_overarching_reference_number:
          item.overarching_reference_number,
        contractualArrangement_linked_contractual_arrangement:
          item.linked_contractual_arrangement,
        contractualArrangement_notice_period: item.notice_period,
        contractualArrangement_governing_law_country_id:
          item.governing_law_country.id,
        contractualArrangement_entity_id: item.entity.id,
        contractualArrangement_third_party_service_provider_id:
          item.third_party_service_provider.id,
      }));
      setData(mappedData);
    } catch (error) {
      console.log(error, 'error');
      setData([]);
    }
  };

  const handleRemoveRow = (key: number) => {
    Modal.confirm({
      title: 'Are you sure you want to remove this row?',
      content:
        'Once deleted, this record cannot be restored. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        const updatedDataSource = dataWithKey.filter(
          (item) => item.key !== key,
        );
        const updatedChangedData = changedData.filter(
          (item) => item.key !== key,
        );
        setChangedData(updatedChangedData);
        setData(updatedDataSource);
        notification.success({
          message: 'Info',
          description: 'The row has been successfully removed.',
          duration: 10,
        });
      },
      onCancel: () => {
        notification.info({
          message: 'Info',
          description: 'The row removal has been canceled.',
          duration: 10,
        });
      },
    });
  };

  const handleInactivate = (record: IContractulInformation) => {
    if (!record.contractualArrangement_id) {
      notification.error({
        message: 'Info',
        description: 'Id is missing.',
      });
      return;
    }

    deleteRecord({
      id: Number(record.contractualArrangement_id),
      tableName: 'contractual_arrangement',
      onSuccess: async () => {
        await fetchContracts();
      },
    });
  };

  const fetchCurrencies = async () => {
    try {
      const response = await entityOp.fetchCurrencies();
      if (response?.data.currencies) {
        const formattedOptions = response.data.currencies.map(
          (reasons: { currency_name: any; id: any }) => ({
            label: reasons.currency_name,
            value: reasons.id,
          }),
        );
        setCurrencies(formattedOptions);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const shouldFetch =
    selectedEntityForContractualInformation &&
    selectedTPPForContractualInformation;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchContractualInformation();
        // TODO
        await Promise.all([
          fetchContracts(),
          fetchTerminatReasons(),
          fetchCurrencies(),
          fetchContractualArrangementTypes(),
        ]);
      } catch (error) {
        console.log('Error during data fetching:', error);
      }
    };
    if (shouldFetch) {
      fetchData();
    }
  }, [
    selectedEntityForContractualInformation,
    selectedTPPForContractualInformation,
    query,
  ]);

  const addNewCost = (rowKey: string | number) => {
    const newCost: ICosts = {
      costs_id: generateUniqueId(),
      costs_currency_amount_reported: '',
      costs_annual_expense: '',
      costs_total_annual_expense: '',
      costs_description: '',
      costs_currency_id: '',
      costs_contractual_arrangement_id: rowKey,
      costs_third_party_service_provider_id:
        selectedTPPForContractualInformation?.value,
      costs_start_date: '',
      costs_end_date: '9999-01-01',
      currency_name: '',
      currency_description: '',
      currency_id: '',
    };

    setData((prevData) =>
      prevData.map((record) => {
        if (record.contractualArrangement_id === rowKey) {
          return {
            ...record,
            costs: [...record.costs, { ...newCost }],
          };
        }
        return record;
      }),
    );
  };

  const dataWithKey = data.map((item) => ({
    ...item,
    key: item.contractualArrangement_id,
  }));

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ['5', '10', '20', '100'],
    position: ['topRight'] as ['topRight'],
  });

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize,
    });
  };

  const addNewRow = async () => {
    const terminatReasons = await fetchTerminatReasons();
    const currencies = await fetchCurrencies();
    const arrangementTypes = await fetchContractualArrangementTypes();

    const contractualArrangement_id = generateUniqueId();
    const newRowTemplate: IContractulInformation = {
      key: contractualArrangement_id,
      isNew: true,
      contract_name: '',
      contract_description: '',
      terminatReason_id: '',
      terminatReason_name: '',
      terminatReason_description: '',
      typeofContractualArrangement_id: '',
      typeofContractualArrangement_name: '',
      typeofContractualArrangement_description: '',
      country_id: '',
      country_country_name: '',
      country_iso_code: '',
      country_description: '',
      contractualArrangement_id: contractualArrangement_id,
      contractualArrangement_arrangement_reference_number: '',
      contractualArrangement_start_date: '',
      contractualArrangement_end_date: '9999-01-01',
      contractualArrangement_terminat_reason_id: '',
      contractualArrangement_contract_type_id: '',
      contractualArrangement_contract_type_arrangement_id: '',
      contractualArrangement_overarching_reference_number: '',
      contractualArrangement_linked_contractual_arrangement: '',
      contractualArrangement_notice_period: 0,
      contractualArrangement_governing_law_country_id: '',
      contractualArrangement_entity_id: query.entityId,
      contractualArrangement_third_party_service_provider_id:
        selectedTPPForContractualInformation?.value,
      contractualArrangement_intra_group: false,
      costs: [
        {
          costs_id: generateUniqueId(),
          costs_currency_amount_reported: '',
          costs_annual_expense: '',
          costs_total_annual_expense: '',
          costs_description: '',
          costs_currency_id: '',
          costs_contractual_arrangement_id: contractualArrangement_id,
          costs_third_party_service_provider_id:
            selectedTPPForContractualInformation?.value,
          costs_start_date: '',
          costs_end_date: '9999-01-01',
          currency_name: '',
          currency_description: '',
          currency_id: '',
        },
      ],
    };

    const newRow = structuredClone(newRowTemplate);

    setData((prevData) => [...prevData, newRow]);
    setChangedData((prevChangedData) => [...prevChangedData, newRow]);
  };
  const handleOpenTooltip = (key: string) => {
    setIsTooltipVisible((prev) => ({ ...prev, [key]: true }));
  };

  const handleCloseTooltip = (key: string) => {
    setIsTooltipVisible((prev) => ({ ...prev, [key]: false }));
  };
  useTooltipClickOutside({
    tooltipVisibility: isTooltipVisible,
    setTooltipVisibility: setIsTooltipVisible,
  });
  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    rowKey: string,
  ) => {
    const { value } = e.target;
    setEditedData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));

    const updatedRow = data.find(
      (row) => row.contractualArrangement_id.toString() == rowKey,
    );

    if (updatedRow) {
      const updatedRowWithChanges: Record<string, any> = {
        ...updatedRow,
        [fieldName]: value,
      };
      if (fieldName === 'costs_id' || 'contract_id') {
        if (updatedRow.costs) {
          const match = fieldName.match(/^(\d+)_c_(.+)$/);
          if (match) {
            const idFromFieldName = match[1];
            const actualFieldName = match[2];
            const targetIndex = updatedRow.costs.findIndex(
              (cost) => cost.costs_id.toString() === idFromFieldName,
            );

            if (targetIndex !== -1) {
              updatedRowWithChanges.costs[targetIndex] = {
                ...updatedRow.costs[targetIndex],
                [actualFieldName]: value,
              };
            }
          }
        }
      }

      const cleanedUpRow = Object.keys(updatedRowWithChanges).reduce(
        (acc: Record<string, any>, key) => {
          const newKey = key.replace(/^[^a-zA-Z]+/, '');
          acc[newKey] = updatedRowWithChanges[key];
          return acc;
        },
        {},
      );

      setChangedData((prevChangedData) => {
        const newChangedData = [...prevChangedData];
        const existingRowIndex = newChangedData.findIndex(
          (row) =>
            row.contractualArrangement_id ===
            cleanedUpRow.contractualArrangement_id,
        );

        if (existingRowIndex !== -1) {
          const existingRow = newChangedData[existingRowIndex];
          const mergedRow = { ...existingRow, ...cleanedUpRow };
          if (JSON.stringify(existingRow) !== JSON.stringify(mergedRow)) {
            newChangedData[existingRowIndex] = mergedRow;
          }
        } else {
          newChangedData.push(cleanedUpRow);
        }

        return newChangedData;
      });
    }
  };
  const updateChangedData = (editedData: any, changedData: any) => {
    if (editedData && typeof editedData === 'object') {
      Object.keys(editedData).forEach((key: string) => {
        const keyFromEditedData = parseInt(key.split('_')[0]);
        const matchingChangedRow = changedData.find((dataItem: any) => {
          return dataItem.contractualArrangement_id === keyFromEditedData;
        });

        if (matchingChangedRow) {
          const fieldName = key.replace(/^\d+_/, '');
          if (matchingChangedRow.hasOwnProperty(fieldName)) {
            matchingChangedRow[fieldName] = editedData[key];
          }
        }
      });
    } else {
      console.log('editedData is not an object:', editedData);
    }

    setChangedData([...changedData]);
  };

  useEffect(() => {
    updateChangedData(editedData, changedData);
  }, [editedData]);

  const handleSubmit = async () => {
    if (changedData.length === 0) {
      notification.info({
        message: 'Info',
        description:
          'There are no new or edited Contractual Informations to submit.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const hasEmptyFields = changedData.some((item) =>
      (requiredFieldsForContractualArrangement as (keyof typeof item)[]).some(
        (field) => item[field] === '' || item[field] === null,
      ),
    );

    if (hasEmptyFields) {
      notification.error({
        message: '',
        description:
          'Please complete all the fields highlighted in yellow and make sure to enter a contract name, start date and country fields before submitting.',
        placement: 'topRight',
        duration: 20,
      });
      return;
    }

    const formattedData = {
      tpp_name: selectedTPPForContractualInformation?.label,
      branch_name: selectedBranchForContractualInformation?.label,
      ict_service_id: selectedICTForContractualInformation?.value,
      entity_name: query.entity,
      contractual_arrangement: changedData.map((item) => {
        return {
          id: item?.contractualArrangement_id,
          arrangement_reference_number:
            item?.contractualArrangement_arrangement_reference_number,
          start_date: item?.contractualArrangement_start_date,
          end_date: item?.contractualArrangement_end_date,
          terminat_reason_id: item?.contractualArrangement_terminat_reason_id,
          contract_type_arrangement_id:
            item?.contractualArrangement_contract_type_arrangement_id,
          overarching_reference_number:
            item?.contractualArrangement_overarching_reference_number,
          linked_contractual_arrangement:
            item?.contractualArrangement_linked_contractual_arrangement,
          notice_period: Number(item?.contractualArrangement_notice_period),
          governing_law_country_id:
            item?.contractualArrangement_governing_law_country_id,
          entity_id: query.entityId,
          third_party_service_provider_id: selectedTPPForContractualInformation
            ? selectedTPPForContractualInformation.value
            : item?.contractualArrangement_third_party_service_provider_id,
          intra_group: item?.contractualArrangement_intra_group,
          contract_name: item?.contract_name,
          contract_description: item?.contract_description,
        };
      }),

      costs: changedData.flatMap(
        (item) =>
          item.costs?.map((cost: ICosts) => ({
            id: cost.costs_id,
            currency_amount_reported: cost.costs_currency_amount_reported,
            annual_expense: Number(cost.costs_annual_expense),
            description: cost.costs_description,
            currency_id: cost.costs_currency_id,
            contractual_arrangement_id: item.contractualArrangement_id,
            start_date: cost.costs_start_date,
            end_date: cost.costs_end_date,
          })) || [],
      ),
      token: isLoggedIn,
    };
    try {
      const response =
        await contractual_informationOp.postContractualInformation(
          formattedData,
        );
      setData((prev) =>
        prev.map((item) => ({ ...item, isNew: false, isEdited: false })),
      );
      setChangedData([]);
    } catch (error) {
      console.log('Submission Error:', error);
      notification.error({
        message: 'Info',
        description: 'There was an error sending the data. Please try again.',
        placement: 'topRight',
        duration: 20,
      });
    }
  };

  const columns: EditableColumnType<IContractulInformation>[] = [
    {
      title: (
        <Styled.TitleComponent>
          {t('CONTRACTUAL_INFORMATION.ICT_TPP')}
          <Styled.Asterisx>*</Styled.Asterisx>
        </Styled.TitleComponent>
      ),
      dataIndex: 'contractualArrangement_third_party_service_provider_id',
      editable: true,
      fixed: 'left',
      render: () => (
        <Input value={selectedTPPForContractualInformation?.label} disabled />
      ),
    },
    {
      title: (
        <Styled.TitleComponent>
          {t('CONTRACTUAL_INFORMATION.CONTRACT_NAME')}
          <Styled.Asterisx>*</Styled.Asterisx>
        </Styled.TitleComponent>
      ),
      dataIndex: 'contract_name',
      editable: true,
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_contract_name`;
        const rowKey = record.key;
        return (
          <Input
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.contract_name
            }
            onChange={(e) => handleEditChange(e, fieldName, rowKey)}
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          {' '}
          {t('CONTRACTUAL_INFORMATION.CONTRACT_DESCRIPTION')}
        </Styled.TitleComponent>
      ),
      dataIndex: 'contract_description',
      editable: true,
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_contract_description`;
        const rowKey = record.key;
        return (
          <Input
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.contract_description
            }
            onChange={(e) => handleEditChange(e, fieldName, rowKey)}
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('CONTRACTUAL_INFORMATION.REF_NUMBER')}</p>
            <p>
              B_02.01.0010, B_02.03.0010, B_03.01.0010, B_03.02.0010,
              B_03.03.0100, B_04.01.0010
            </p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('CONTRACTUAL_INFORMATION.IDENTIFICATION')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'contractualArrangement_arrangement_reference_number',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_contractualArrangement_arrangement_reference_number`;
        const rowKey = record.key;
        return (
          <Input
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.contractualArrangement_arrangement_reference_number
            }
            onChange={(e) => handleEditChange(e, fieldName, rowKey)}
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('CONTRACTUAL_INFORMATION.VALID_FROM')}</p>
            <p>B_02.02.0070 / B_02.02.0080</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          {/* Start Date Info */}
          <AdditionalInformation
            color="red"
            description={t('CONTRACTUAL_INFORMATION.VALID_FROM_INFO')}
          />
          {t('CONTRACTUAL_INFORMATION.VALID_TO')}
          <AdditionalInformation
            color="red"
            description={t('CONTRACTUAL_INFORMATION.VALID_TO_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'contractualArrangement_date_range',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const startFieldName = `${record.contractualArrangement_id}_contractualArrangement_start_date`;
        const endFieldName = `${record.contractualArrangement_id}_contractualArrangement_end_date`;

        return (
          <Styled.DateInputs>
            <DynamicField
              value={
                editedData.hasOwnProperty(startFieldName)
                  ? String(editedData[startFieldName] ?? '')
                  : String(record.contractualArrangement_start_date ?? '')
              }
              fieldName={startFieldName}
              rowKey={record.key}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleEditChange(e, startFieldName, record.key)
              }
              type="date"
              useDefaultDate={false}
            />
            <DynamicField
              value={
                editedData.hasOwnProperty(endFieldName)
                  ? String(editedData[endFieldName] ?? '9999-01-01')
                  : String(
                      record.contractualArrangement_end_date ?? '9999-01-01',
                    )
              }
              fieldName={endFieldName}
              rowKey={record.key}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleEditChange(e, endFieldName, record.key)
              }
              type="date"
              useDefaultDate={true}
            />
          </Styled.DateInputs>
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('CONTRACTUAL_INFORMATION.OVERARCHING')}</p>
            <p>B_02.01.0030</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('CONTRACTUAL_INFORMATION.OVERARCHING_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'contractualArrangement_overarching_reference_number',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_contractualArrangement_overarching_reference_number`;
        const rowKey = record.key;
        return (
          <Input
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.contractualArrangement_overarching_reference_number
            }
            onChange={(e) => handleEditChange(e, fieldName, rowKey)}
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          {t('CONTRACTUAL_INFORMATION.CONTRACT_LINKED')}
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('CONTRACTUAL_INFORMATION.CONTRACT_LINKED_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'contractualArrangement_linked_contractual_arrangement',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_contractualArrangement_linked_contractual_arrangement`;
        const rowKey = record.key;
        return (
          <Input
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.contractualArrangement_linked_contractual_arrangement
            }
            onChange={(e) => handleEditChange(e, fieldName, rowKey)}
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('CONTRACTUAL_INFORMATION.NOTICE_PERIOD')}</p>
            <p>B_02.02.0100</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('CONTRACTUAL_INFORMATION.NOTICE_PERIOD_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'contractualArrangement_notice_period',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_contractualArrangement_notice_period`;
        const rowKey = record.key;
        return (
          <Input
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.contractualArrangement_notice_period
            }
            onChange={(e) => handleEditChange(e, fieldName, rowKey)}
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('CONTRACTUAL_INFORMATION.REASON_OF_TERMINATION')}</p>
            <p>B_02.02.0090</p>
          </div>
          <AdditionalInformation
            color="red"
            description={t(
              'CONTRACTUAL_INFORMATION.REASON_OF_TERMINATION_INFO',
            )}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'terminatReason_name',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_terminatReason_name`;
        const fieldNameId = `${record.contractualArrangement_id}_terminatReason_id`;
        const fieldNameIdContract = `${record.contractualArrangement_id}_contractualArrangement_terminat_reason_id`;

        const rowKey = record.key;
        return (
          <CustomSelect
            options={terminatReasons}
            placeholder="Select an option"
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.terminatReason_name
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const idEvent = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(idEvent, fieldNameId, rowKey);
              handleEditChange(idEvent, fieldNameIdContract, rowKey);
              const nameEvent = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(nameEvent, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          {t('CONTRACTUAL_INFORMATION.INTRA_GROUP')}
          <Styled.Asterisx>*</Styled.Asterisx>
        </Styled.TitleComponent>
      ),
      dataIndex: 'contractualArrangement_intra_group',
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_contractualArrangement_intra_group`;
        const rowKey = record.key;
        return (
          <CustomSelect
            options={intraGroup}
            placeholder="Select an option"
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.contractualArrangement_intra_group
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const nameEvent = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(nameEvent, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p> {t('CONTRACTUAL_INFORMATION.TYPE_OF_CONTRACT')}</p>
            <p>B_02.01.0020</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t(
              'CONTRACTUAL_INFORMATION.TYPE_OF_CONTRACTUAL_ARRANGEMENT_INFO',
            )}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'typeofContractualArrangement_name',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_typeofContractualArrangement_name`;
        const fieldNameId = `${record.contractualArrangement_id}_typeofContractualArrangement_id`;
        const fieldNameIdArrangement = `${record.contractualArrangement_id}_contractualArrangement_contract_type_arrangement_id`;
        const fieldNameId2 = `${record.contractualArrangement_id}_contractualArrangement_contract_type_id`;

        const rowKey = record.key;
        return (
          <CustomSelect
            options={contractualArrangementTypes}
            placeholder="Select an option"
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.typeofContractualArrangement_name
            }
            onChange={(
              value: string,
              option: { label: string; value: string },
            ) => {
              const idEvent = {
                target: {
                  value: option.value,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(idEvent, fieldNameId, rowKey);
              handleEditChange(idEvent, fieldNameIdArrangement, rowKey);
              handleEditChange(idEvent, fieldNameId2, rowKey);

              const nameEvent = {
                target: {
                  value: option.label,
                },
              } as React.ChangeEvent<HTMLInputElement>;
              handleEditChange(nameEvent, fieldName, rowKey);
            }}
            size="large"
            filterOption={(input: string, option: { label: string }) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          {t('CONTRACTUAL_INFORMATION.COSTS')}
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('CONTRACTUAL_INFORMATION.COSTS_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'costs',
      key: 'costs',
      className: 'highlighted-column',
      render: (_, record) => {
        return (
          <div>
            {(record.costs as ICosts[]).map((cost: ICosts) => {
              const idFieldName = `${cost.costs_id}_c_costs_id`;
              const currencyId = `${cost.costs_id}_c_costs_currency_id`;
              const currencyAmountReportedFieldName = `${cost.costs_id}_c_costs_currency_amount_reported`;
              const annualExpenseFieldName = `${cost.costs_id}_c_costs_annual_expense`;
              const descriptionFieldName = `${cost.costs_id}_c_costs_description`;
              const startDateFieldName = `${cost.costs_id}_c_costs_start_date`;
              const endDateFieldName = `${cost.costs_id}_c_costs_end_date`;
              const rowKey = record.key;
              const handleRemoveCost = (costKey: number, costID: number) => {
                Modal.confirm({
                  title: t('CONTRACTUAL_INFORMATION.SURE_REMOVE'),
                  content: t('CONTRACTUAL_INFORMATION.ACTION_CANNOT_BE_UNDONE'),
                  okText: t('GENERAL.YES'),
                  cancelText: t('GENERAL.NO'),
                  onOk: async () => {
                    try {
                      deleteRecord({
                        id: costID,
                        tableName: 'costs',
                        onSuccess: async () => {
                          const updatedContracts = data.map((data) => {
                            if (data.key === costKey) {
                              const updatedData = data.datas
                                ? data.datas.filter(
                                    (cost: ICosts) => cost.costs_id !== costID,
                                  )
                                : [];

                              return {
                                ...data,
                                datas: updatedData,
                              };
                            }
                            return data;
                          });
                          setData(updatedContracts);
                          if (record.isNew) {
                            const updatedContracts = data.map((item) => {
                              const filteredCosts =
                                item.costs?.filter(
                                  (cost: ICosts) => cost.costs_id !== costID,
                                ) || [];

                              return {
                                ...item,
                                costs: filteredCosts,
                              };
                            });

                            setData(updatedContracts);
                          } else {
                            fetchContracts();
                          }
                        },
                      });
                      // const response =
                      //   await contractual_informationOp.inactivateCosts([
                      //     costID,
                      //   ]);
                    } catch (error) {
                      console.log('Failed to remove the Cost:', error);
                    }
                  },
                  onCancel: () => {
                    notification.info({
                      message: 'Info',
                      description: t(
                        'CONTRACTUAL_INFORMATION.THE_REMOVAL_CANCELED',
                      ),
                      duration: 10,
                    });
                  },
                });
              };
              return (
                <Styled.TooltipContainer
                  key={cost.costs_id}
                  visible={isTooltipVisible[idFieldName]}
                  title={
                    <div>
                      <Styled.TooltipButtonContainer>
                        <Button
                          size="small"
                          type="text"
                          danger
                          onClick={() =>
                            handleRemoveCost(record.key, cost.costs_id)
                          }
                        >
                          <DeleteOutlined />
                        </Button>
                        <Button
                          size="small"
                          type="text"
                          onClick={() => handleCloseTooltip(idFieldName)}
                        >
                          {t('GENERAL.ADD_AMEND')}
                        </Button>
                      </Styled.TooltipButtonContainer>
                      <Styled.InputField>
                        <strong>
                          <Styled.TitleComponent>
                            {t('CONTRACTUAL_INFORMATION.CURRENCY_AMOUNT')}
                            <Styled.Asterisx>*</Styled.Asterisx>
                            <AdditionalInformation
                              color="red"
                              description={t(
                                'CONTRACTUAL_INFORMATION.CURRENCY_AMOUNT_INFO',
                              )}
                            />
                          </Styled.TitleComponent>
                        </strong>
                        <CustomSelect
                          value={
                            editedData.hasOwnProperty(
                              currencyAmountReportedFieldName,
                            )
                              ? editedData[currencyAmountReportedFieldName]
                              : cost.costs_currency_amount_reported
                          }
                          options={currencies}
                          onChange={(
                            value: string,
                            option: { label: string; value: string },
                          ) => {
                            const idEvent = {
                              target: {
                                value: option.value,
                              },
                            } as React.ChangeEvent<HTMLInputElement>;
                            handleEditChange(idEvent, currencyId, rowKey);

                            const nameEvent = {
                              target: {
                                value: option.label,
                              },
                            } as React.ChangeEvent<HTMLInputElement>;
                            handleEditChange(
                              nameEvent,
                              currencyAmountReportedFieldName,
                              rowKey,
                            );
                          }}
                          filterOption={(
                            input: string,
                            option: { label: string },
                          ) =>
                            option?.label
                              ?.toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        />
                      </Styled.InputField>
                      <Styled.InputField>
                        <strong>
                          <Styled.TitleComponent>
                            {t('CONTRACTUAL_INFORMATION.ANNUAL_EXPENSE')}

                            <Styled.Asterisx>*</Styled.Asterisx>
                            <AdditionalInformation
                              color="red"
                              description={t(
                                'CONTRACTUAL_INFORMATION.ANNUAL_EXPENSE_INFO',
                              )}
                            />
                          </Styled.TitleComponent>
                        </strong>
                        <Input
                          value={
                            editedData.hasOwnProperty(annualExpenseFieldName)
                              ? typeof editedData[annualExpenseFieldName] ===
                                'boolean'
                                ? editedData[annualExpenseFieldName]
                                  ? 'true'
                                  : 'false'
                                : String(editedData[annualExpenseFieldName])
                              : String(cost.costs_annual_expense)
                          }
                          onChange={(e) =>
                            handleEditChange(e, annualExpenseFieldName, rowKey)
                          }
                        />
                      </Styled.InputField>

                      <Styled.InputField>
                        <strong>
                          {t('CONTRACTUAL_INFORMATION.COST_VALID_FROM')}{' '}
                          <Styled.Asterisx>*</Styled.Asterisx>
                        </strong>
                        <DynamicField
                          value={(() => {
                            const resolvedValue = editedData.hasOwnProperty(
                              startDateFieldName,
                            )
                              ? String(editedData[startDateFieldName])
                              : String(cost.costs_start_date);
                            return resolvedValue;
                          })()}
                          fieldName={startDateFieldName}
                          rowKey={record.key}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleEditChange(e, startDateFieldName, record.key)
                          }
                          type="date"
                          useDefaultDate={false}
                        />
                      </Styled.InputField>
                      <Styled.InputField>
                        <strong>
                          {t('CONTRACTUAL_INFORMATION.COST_VALID_TO')}{' '}
                          <Styled.Asterisx>*</Styled.Asterisx>
                        </strong>
                        <DynamicField
                          value={
                            editedData.hasOwnProperty(endDateFieldName)
                              ? String(
                                  editedData[endDateFieldName] ?? '9999-01-01',
                                )
                              : String(cost.costs_end_date ?? '9999-01-01')
                          }
                          fieldName={endDateFieldName}
                          rowKey={record.key}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleEditChange(e, endDateFieldName, record.key)
                          }
                          type="date"
                          useDefaultDate={true}
                        />
                      </Styled.InputField>
                      <Styled.InputField>
                        <strong>
                          {t('CONTRACTUAL_INFORMATION.DESCRIPTION')}
                        </strong>
                        <Input
                          value={
                            editedData.hasOwnProperty(descriptionFieldName)
                              ? typeof editedData[descriptionFieldName] ===
                                'boolean'
                                ? editedData[descriptionFieldName]
                                  ? 'true'
                                  : 'false'
                                : String(editedData[descriptionFieldName])
                              : String(cost.costs_description)
                          }
                          onChange={(e) =>
                            handleEditChange(e, descriptionFieldName, rowKey)
                          }
                        />
                      </Styled.InputField>
                    </div>
                  }
                  open={isTooltipVisible[idFieldName] || false}
                  trigger={[]}
                >
                  <Styled.TagComponent
                    color="blue"
                    onClick={() => handleOpenTooltip(idFieldName)}
                  >
                    <Input
                      value={
                        editedData.hasOwnProperty(annualExpenseFieldName)
                          ? typeof editedData[annualExpenseFieldName] ===
                            'boolean'
                            ? editedData[annualExpenseFieldName]
                              ? 'true'
                              : 'false'
                            : String(editedData[annualExpenseFieldName])
                          : String(cost.costs_annual_expense)
                      }
                      readOnly
                      addonAfter={<FolderOpenOutlined />}
                    />
                  </Styled.TagComponent>
                  <Styled.GlobalStyle />
                </Styled.TooltipContainer>
              );
            })}
            <Styled.AddButton
              type="text"
              icon={<PlusOutlined />}
              onClick={() => addNewCost(record.key)}
            />
          </div>
        );
      },
    },
    {
      title: (
        <Styled.TitleComponent>
          <div>
            <p>{t('CONTRACTUAL_INFORMATION.COUNTRY')}</p>
            <p>B_02.02.0120</p>
          </div>
          <Styled.Asterisx>*</Styled.Asterisx>
          <AdditionalInformation
            color="red"
            description={t('CONTRACTUAL_INFORMATION.COUNTRY_INFO')}
          />
        </Styled.TitleComponent>
      ),
      dataIndex: 'country_country_name',
      editable: true,

      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_country_country_name`;
        const fieldNameId = `${record.contractualArrangement_id}_country_id`;
        const fieldNameIdGoverningLaw = `${record.contractualArrangement_id}_contractualArrangement_governing_law_country_id`;
        const rowKey = record.key;

        return (
          <>
            <CustomSelect
              value={
                editedData.hasOwnProperty(fieldName)
                  ? editedData[fieldName]
                  : record.country_country_name
              }
              options={countries}
              onChange={(
                value: string,
                option: { label: string; value: string },
              ) => {
                const idEvent = {
                  target: {
                    value: option.value,
                  },
                } as React.ChangeEvent<HTMLInputElement>;
                handleEditChange(idEvent, fieldNameId, rowKey);
                handleEditChange(idEvent, fieldNameIdGoverningLaw, rowKey);

                const nameEvent = {
                  target: {
                    value: option.label,
                  },
                } as React.ChangeEvent<HTMLInputElement>;
                handleEditChange(nameEvent, fieldName, rowKey);
                const selectedCountry = countries.find(
                  (country) => country.value === value,
                );
                if (selectedCountry) {
                  const newIsoCode = i18nIsoCountries.getAlpha2Code(
                    selectedCountry.label,
                    'en',
                  );
                  const isoEvent = {
                    target: { value: newIsoCode || '' },
                  } as React.ChangeEvent<HTMLInputElement>;
                  handleEditChange(
                    isoEvent,
                    `${record.contractualArrangement_id}_country_iso_code`,
                    rowKey,
                  );
                }
              }}
              filterOption={(input: string, option: { label: string }) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </>
        );
      },
    },
    {
      title: <>{t('CONTRACTUAL_INFORMATION.SELECTED_COUNTRY_ISO')}</>,
      dataIndex: 'country_iso_code',
      editable: true,
      className: 'highlighted-column',
      render: (_: any, record: any) => {
        const fieldName = `${record.contractualArrangement_id}_country_iso_code`;
        const rowKey = record.key;
        return (
          <Input
            value={
              editedData.hasOwnProperty(fieldName)
                ? editedData[fieldName]
                : record.country_iso_code
            }
            onChange={(e) => handleEditChange(e, fieldName, rowKey)}
            disabled
          />
        );
      },
    },
    {
      title: t('GENERAL.ACTIONS'),
      key: 'actions',
      render: (_: any, record) => (
        <>
          {record.isNew ? (
            <Button danger onClick={() => handleRemoveRow(record.key)}>
              {t('GENERAL.REMOVE')}
            </Button>
          ) : (
            <Button
              danger
              onClick={() => handleInactivate(record as IContractulInformation)}
            >
              {t('GENERAL.SOFT_DELETE')}
            </Button>
          )}
        </>
      ),
    },
  ];
  useEffect(() => {
    const findScrollable = () => {
      const el = document.querySelector(
        '.ant-table-body, .ant-table-content',
      ) as HTMLDivElement | null;

      if (el) {
        tableContentRef.current = el;
      }
    };

    findScrollable();
    const observer = new MutationObserver(findScrollable);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const top = topScrollRef.current;
    const table = tableContentRef.current;

    if (!top || !table) return;

    const syncTop = () => {
      if (top) top.scrollLeft = table.scrollLeft;
    };
    const syncBottom = () => {
      if (table) table.scrollLeft = top.scrollLeft;
    };

    top.addEventListener('scroll', syncBottom);
    table.addEventListener('scroll', syncTop);

    return () => {
      top.removeEventListener('scroll', syncBottom);
      table.removeEventListener('scroll', syncTop);
    };
  }, [dataWithKey, columns]);

  useEffect(() => {
    const table = tableContentRef.current;
    if (table && topScrollInnerRef.current) {
      topScrollInnerRef.current.style.width = `${table.scrollWidth}px`;
    }
  }, [dataWithKey, columns]);

  return (
    <>
      {/* <Styled.ButtonDiv isEmpty={dataWithKey.length === 0}>
      {query.entity &&
          selectedICTForContractualInformation === null &&
          selectedTPPForContractualInformation && (
            <Styled.AreaButton token={token} type="primary" onClick={addNewRow}>
              Add Row
            </Styled.AreaButton>
          )}
      </Styled.ButtonDiv> */}
      <Styled.TopScrollBar ref={topScrollRef}>
        <div ref={topScrollInnerRef} />
      </Styled.TopScrollBar>
      <Styled.TooltipTableContainer>
        <Table
          dataSource={dataWithKey}
          rowKey="key"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: handlePaginationChange,
            showSizeChanger: pagination.showSizeChanger,
            pageSizeOptions: pagination.pageSizeOptions,
            position: pagination.position,
          }}
          columns={columns}
          locale={
            query.entity &&
            selectedICTForContractualInformation === null &&
            selectedTPPForContractualInformation
              ? {
                  emptyText: (
                    <Button type="primary" onClick={() => addNewRow()}>
                      {t('GENERAL.ADD_FIRST_ROW')}
                    </Button>
                  ),
                }
              : undefined
          }
        />
      </Styled.TooltipTableContainer>
      <Styled.ButtonRow>
        {query.entity &&
          selectedICTForContractualInformation === null &&
          selectedTPPForContractualInformation &&
          dataWithKey.length > 0 && (
            <Styled.AreaButton
              token={token}
              type="primary"
              onClick={() => addNewRow()}
            >
              Add new row
            </Styled.AreaButton>
          )}
        {dataWithKey.length > 0 && (
          <SaveButton
            token={token}
            hasChanges={changedData.length > 0}
            onClick={handleSubmit}
          />
        )}
      </Styled.ButtonRow>
    </>
  );
};

export default ContractualInformation;
